import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  KeyIcon, 
  GlobeAltIcon, 
  ArrowPathIcon, 
  LockClosedIcon,
  LockOpenIcon,
  SparklesIcon,
  ClipboardIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { makeApiRequest } from '../utils/api';

function CreateRoom() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [roomType, setRoomType] = useState(null);
  const [roomName, setRoomName] = useState('');
  const [pin, setPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [createdRoom, setCreatedRoom] = useState(null);
  const [copied, setCopied] = useState(false);
  const [formError, setFormError] = useState(null);

  const generateRoomName = () => {
    const adjectives = ['Happy', 'Clever', 'Bright', 'Swift', 'Cool', 'Vibrant', 'Cosmic', 'Mystic', 'Radiant', 'Stellar'];
    const nouns = ['Dolphin', 'Tiger', 'Eagle', 'Lion', 'Wolf', 'Phoenix', 'Galaxy', 'Summit', 'Ocean', 'Aurora'];
    const number = Math.floor(Math.random() * 1000);
    
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    
    setRoomName(`${randomAdjective}${randomNoun}${number}`);
  };

  const handleRoomTypeSelect = (type) => {
    setRoomType(type);
    setStep(2); // Move to room name input step
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    
    if (!roomName.trim()) {
      toast.error('Please enter a room name');
      return;
    }
    
    if (roomType === 'private' && (!pin || pin.length !== 4)) {
      toast.error('Please enter a 4-digit PIN for private rooms');
      return;
    }
    
    const nickname = localStorage.getItem('chatNickname');
    if (!nickname) {
      navigate('/');
      return;
    }
    
    setIsLoading(true);
    setFormError(null);
    
    try {
      // Prepare room data
      const roomData = {
        name: roomName.trim(),
        isPrivate: roomType === 'private',
        createdBy: nickname
      };
      
      // Add PIN for private rooms
      if (roomType === 'private' && pin) {
        roomData.pin = pin;
      }
      
      console.log('Creating room:', { ...roomData, pin: roomData.pin ? '****' : undefined });
      
      // Make API request using our helper function
      const data = await makeApiRequest('api/rooms/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roomData)
      });
      
      // Get room ID (either from roomId field or data.data structure)
      const roomId = data.roomId || (data.data && data.data.roomId);
      const roomInfo = data.room || (data.data && data.data.room);
      
      if (!roomId || !roomInfo) {
        throw new Error('Server did not return room information');
      }
      
      // Success: Show toast and save created room info for success card
      toast.success(data.message || 'Room created successfully!');
      
      // Set created room data for Step 3
      setCreatedRoom({
        id: roomId,
        name: roomInfo.name,
        isPrivate: roomInfo.isPrivate,
        pin: roomType === 'private' ? pin : undefined,
        createdBy: roomInfo.createdBy,
        createdAt: roomInfo.createdAt
      });
      
      // Move to success step instead of navigating directly to room
      setStep(3);
    } catch (error) {
      console.error('Error creating room:', error);
      setFormError(error.message);
      toast.error(error.message || 'Failed to create room. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleJoinRoom = () => {
    navigate(`/chat/${createdRoom.id}`);
  };

  // Step 1: Choose Room Type
  const renderRoomTypeSelection = () => (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-4xl font-bold text-center mb-8 text-gray-800 dark:text-white">
        What type of room do you want to create?
        </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Public Room Card */}
        <button
          onClick={() => handleRoomTypeSelect('public')}
          className="group relative h-80 overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:border-blue-200 dark:hover:border-blue-700"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/80 to-transparent dark:from-blue-900/10 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="p-8 h-full flex flex-col items-center justify-center relative z-10">
            <div className="p-5 mb-5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 group-hover:shadow-lg group-hover:shadow-blue-200/30 dark:group-hover:shadow-blue-900/20 transition-all">
              <GlobeAltIcon className="w-14 h-14" />
            </div>
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Public Room</h2>
            <p className="text-gray-600 dark:text-gray-300 text-center">
              Create a room that anyone can join. Room will be visible in the public list.
            </p>
            <div className="absolute bottom-6 right-6">
              <LockOpenIcon className="w-10 h-10 text-blue-400 dark:text-blue-500 opacity-20" />
            </div>
          </div>
        </button>

        {/* Private Room Card */}
        <button
          onClick={() => handleRoomTypeSelect('private')}
          className="group relative h-80 overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:border-purple-200 dark:hover:border-purple-700"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-50/80 to-transparent dark:from-purple-900/10 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="p-8 h-full flex flex-col items-center justify-center relative z-10">
            <div className="p-5 mb-5 rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-500 dark:text-purple-400 group-hover:shadow-lg group-hover:shadow-purple-200/30 dark:group-hover:shadow-purple-900/20 transition-all">
              <KeyIcon className="w-14 h-14" />
            </div>
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Private Room</h2>
            <p className="text-gray-600 dark:text-gray-300 text-center">
              Create a secure room with PIN protection. Only invited users can join.
            </p>
            <div className="absolute bottom-6 right-6">
              <LockClosedIcon className="w-10 h-10 text-purple-400 dark:text-purple-500 opacity-20" />
            </div>
          </div>
        </button>
      </div>
    </div>
  );

  // Step 2: Enter Room Details
  const renderRoomDetailsForm = () => (
    <div className="max-w-3xl mx-auto">
      <button 
        onClick={handleBack}
        className="mb-6 flex items-center text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to room type
      </button>

      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-8 relative overflow-hidden">
        {/* Background glow effect */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-400/20 dark:bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-purple-400/20 dark:bg-purple-600/20 rounded-full blur-3xl"></div>
        
        <div className="relative z-10">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-800 dark:text-white">
            {roomType === 'public' ? 'Create Public Room' : 'Create Private Room'}
          </h2>

          <form onSubmit={handleCreateRoom} className="space-y-8">
            {/* Room Name Input with improved UI */}
            <div className="space-y-4">
              <label className="block text-xl font-medium text-gray-700 dark:text-gray-200">
              Room Name
            </label>
              
              <div className="relative">
              <input
                type="text"
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
                  className="w-full text-lg px-6 py-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 
                  bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white
                  focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 
                  focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/50
                  transition-all shadow-inner"
                  placeholder="Enter a memorable room name..."
                required
              />
                
              <button
                type="button"
                onClick={generateRoomName}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 flex items-center justify-center 
                  border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800
                  text-gray-500 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400
                  hover:border-blue-200 dark:hover:border-blue-700 transition-all"
              >
                  <SparklesIcon className="w-5 h-5 mr-1" />
                  <span className="text-sm">Generate</span>
              </button>
            </div>
              
              <p className="text-sm text-gray-500 dark:text-gray-400 pl-1">
                {roomType === 'public' 
                  ? 'This will be visible to everyone in the room list.'
                  : 'Only users with the room name and PIN can join.'}
            </p>
          </div>

          {/* PIN Input for Private Rooms */}
            {roomType === 'private' && (
              <div className="space-y-4">
                <label className="block text-xl font-medium text-gray-700 dark:text-gray-200">
                  Room PIN
              </label>
                <div className="relative">
              <input
                type="text"
                value={pin}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setPin(value);
                }}
                    className="w-full text-lg px-6 py-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 
                    bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white
                    focus:outline-none focus:border-purple-500 dark:focus:border-purple-400 
                    focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-900/50
                    transition-all shadow-inner tracking-widest text-center"
                    placeholder="4-digit PIN"
                    required
                pattern="\d{4}"
                maxLength={4}
              />
                  <KeyIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 pl-1">
                  Remember to share this PIN with the people you want to invite.
                </p>
            </div>
          )}

          <button
            type="submit"
              disabled={isLoading || (roomType === 'private' && pin.length !== 4) || !roomName.trim()}
              className="w-full mt-8 py-4 px-6 rounded-xl text-lg font-medium
              bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700
              text-white shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              disabled:opacity-70 disabled:cursor-not-allowed transition-all
              transform hover:-translate-y-1 active:translate-y-0"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating your room...
                </span>
              ) : (
                `Create ${roomType === 'public' ? 'Public' : 'Private'} Room`
              )}
          </button>
        </form>
        </div>
      </div>
    </div>
  );

  // Step 3: Success Card
  const RoomSuccessCard = ({ room, onContinue }) => {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 p-8 w-full max-w-md animate-fade-in mx-auto">
        <div className="flex flex-col items-center mb-6">
          <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
            <CheckCircleIcon className="h-10 w-10 text-green-500 dark:text-green-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white text-center">
            Room Created Successfully!
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-center mt-2">
            Your chat room has been created and is ready to use.
          </p>
        </div>
        
        <div className="mb-6 space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-5 space-y-4 shadow-inner">
            <div>
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Room Name</div>
              <div className="font-medium text-gray-800 dark:text-gray-200 text-lg">{room.name}</div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Room Type</div>
              <div className="font-medium text-gray-800 dark:text-gray-200 flex items-center">
                {room.isPrivate ? (
                  <>
                    <LockClosedIcon className="h-4 w-4 mr-1 text-purple-500 dark:text-purple-400" />
                    <span>Private Room</span>
                  </>
                ) : (
                  <>
                    <GlobeAltIcon className="h-4 w-4 mr-1 text-blue-500 dark:text-blue-400" />
                    <span>Public Room</span>
                  </>
                )}
              </div>
            </div>
            
            {room.isPrivate && (
              <div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">PIN Code</div>
                <div className="font-medium text-gray-800 dark:text-gray-200 flex items-center">
                  <div className="bg-gray-100 dark:bg-gray-800 py-1 px-4 rounded tracking-widest text-lg font-mono">{room.pin}</div>
                  <button 
                    onClick={() => copyToClipboard(room.pin)}
                    className="ml-2 p-1 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400"
                    title="Copy PIN"
                  >
                    <ClipboardIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
            
            <div>
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Created By</div>
              <div className="font-medium text-gray-800 dark:text-gray-200">{room.createdBy}</div>
            </div>
          </div>
          
          <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Important Information</h4>
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
              <p className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <span>
                  Messages will be stored for <span className="font-medium text-gray-800 dark:text-gray-200">1 day</span> in public rooms and <span className="font-medium text-gray-800 dark:text-gray-200">7 days</span> in private rooms.
                </span>
              </p>
              <p className="flex items-start">
                <ClockIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <span>
                  Rooms without activity for <span className="font-medium text-gray-800 dark:text-gray-200">7 days</span> will be automatically deleted.
                </span>
              </p>
              <p className="flex items-start">
                <ShieldCheckIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <span>
                  {room.isPrivate ? 
                    "To join this private room, both the room name and PIN will be required." : 
                    "Anyone with the room name can join this public room."}
                </span>
              </p>
            </div>
          </div>
        </div>
        
        <button
          onClick={onContinue}
          className="w-full py-4 px-6 flex items-center justify-center bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 
          text-white rounded-xl font-medium shadow-lg transition-all
          hover:shadow-xl hover:shadow-blue-500/20 dark:hover:shadow-blue-600/20 hover:from-blue-600 hover:to-indigo-700
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <span>Continue to Chat</span>
          <ArrowRightIcon className="h-5 w-5 ml-2" />
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-indigo-950/30 dark:to-purple-950/30 flex flex-col items-center justify-center p-4 sm:p-8">
      {/* Background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5 dark:opacity-10"></div>
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-200/20 via-transparent to-transparent dark:from-blue-800/10"></div>
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto pt-8 pb-8">
        {step === 1 ? (
          renderRoomTypeSelection()
        ) : step === 2 ? (
          renderRoomDetailsForm()
        ) : (
          <div className="flex items-center justify-center min-h-[80vh]">
            <RoomSuccessCard 
              room={createdRoom}
              onContinue={handleJoinRoom}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateRoom;
